import type { Messages } from "./en";
import { evidenceZhTW } from "./evidence";

export const zhTW = {
  evidence: evidenceZhTW,
  brand: { name: "CAYLET", tool: "IP 檢測" },
  nav: {
    check: "IP 保護檢測",
    how: "原理解析",
    esim: "自研eSIM",
    articles: "科普文章",
    main: "Caylet.com",
    language: "選擇語言",
    languageCodes: { en: "EN", zhCN: "简", zhTW: "繁" },
    languageNames: { en: "English", zhCN: "简体中文", zhTW: "繁體中文" }
  },
  scanner: {
    title: "IP 信譽與網路環境檢測",
    body: "Cloudflare 會觀察本次請求使用的連線；啟用 Scamalytics 後，同一個公網 IP 會傳送給該服務以查詢信譽。",
    start: "開始 IP 檢測",
    rescan: "再次檢測",
    loadingLocal: "正在讀取瀏覽器提供的資訊…",
    loadingNetwork: "正在請求網路觀察結果…",
    complete: "檢測完成",
    partial: "檢測完成，部分欄位無法取得",
    consent: "本頁面在載入時會透過 Cloudflare 連線資訊自動對你目前的公網 IP 進行檢測。",
    privacy: "檢測結果僅保留在目前分頁記憶體中，且不會因切換語言而重新請求。",
    statusSafe: "檢測完成；目前可用資料未發現明顯警示。",
    statusWarning: "環境參數檢測完成，偵測到中度網路風控風險。",
    statusDanger: "環境參數檢測完成，偵測到高度網路風控風險。"
  },
  common: {
    unavailable: "無法取得",
    yes: "是",
    no: "否",
    unknown: "未知",
    approximate: "近似",
    observed: "已觀察到",
    notObserved: "未觀察到",
    close: "關閉"
  },
  cards: {
    dashboardTitle: "網路環境與設備綜合檢測儀表盤",
    dashboardSub: "GLOBAL NETWORK & ENVIRONMENT DIAGNOSTIC MATRIX",
    connection: "連線摘要",
    connectionSub: "CONNECTION SUMMARY",
    reputation: "IP 信譽",
    reputationSub: "IP REPUTATION",
    browser: "瀏覽器環境",
    browserSub: "BROWSER ENVIRONMENT",
    hardware: "硬體與系統規格",
    hardwareSub: "HARDWARE & SYSTEM SPECIFICATIONS",
    protocol: "協定觀察",
    protocolSub: "PROTOCOL OBSERVATIONS",
    ip: "公網 IP",
    family: "IP 類型",
    location: "IP 所在地",
    asn: "ASN",
    organization: "機構",
    ipTimezone: "IP 所在時區",
    score: "IP 欺詐值",
    band: "風險評級",
    flags: "資料供應商標記",
    providerTime: "資料時間",
    abuseScore: "公網威脅置信度",
    abuseReports: "公網惡意檢舉次數",
    proxy: "Proxy",
    vpn: "VPN",
    tor: "Tor",
    datacenter: "資料中心",
    deviceTimezone: "裝置時區",
    language: "瀏覽器語言",
    browserName: "瀏覽器",
    os: "作業系統",
    screen: "螢幕",
    cores: "CPU 邏輯核心數",
    currentFamily: "目前連線類型",
    webrtc: "WebRTC 觀察",
    noAdditionalIp: "本次測試未觀察到額外公網 IP",
    additionalIp: "WebRTC 觀察到額外公網 IP，請結合路由環境檢查：",
    unsupported: "此瀏覽器未提供所需的 WebRTC 介面。",
    connectionAttribute: "IP 所在地區",
    verifiedUS: "連線特徵：美國地區 (US)",
    verifiedNonUS: "連線特徵：非美國地區",
    nodeAssessment: "節點特徵",
    nodeClear: "[未見標記] 目前可用供應商欄位均為否",
    nodeRisk: "[警示] 代理/機房節點",
    tzConsistency: "時區一致性",
    tzMatch: "[正常] 時區參數符合",
    tzMismatch: "[警示] 偵測到時區不一致",
    gpu: "顯示晶片 (GPU)",
    gpuStatus: "GPU 渲染器分類",
    gpuUnclassified: "[已觀察] 已取得渲染器，硬體類型未經驗證",
    virtualGpu: "[警示] 虛擬機 / 雲端模擬器 GPU",
    webdriver: "自動化操控標記",
    webdriverDetected: "[警告] 自動化操控特徵 (WebDriver)",
    webdriverNotExposed: "[已觀察] 未暴露 WebDriver 標記",
    pingTest: "網路延遲測試 (Ping)",
    pingTestSub: "CONNECTION LATENCY",
    pingFast: "極速",
    pingGood: "良好",
    pingSlow: "緩慢",
    pingFailed: "超時",
    pingUnit: "毫秒",
    pingTargets: {
      cloudflareEdge: "Cloudflare 邊緣網路",
      cayletApi: "Caylet API"
    },
    pingMethod: "測量此瀏覽器到 Caylet Cloudflare 邊緣網路與 Worker 的同源 HTTPS 往返時間，並非 ICMP Ping。",
    pingExplanationLabel: "解釋網路延遲測量方式",
    pingExplanation: "瀏覽器會向目前的 Caylet 網站傳送兩個不使用快取的小型 HTTPS 請求。「Cloudflare 邊緣網路」測量靜態檔案請求，「Caylet API」測量 Worker 健康檢查端點。從送出請求到收到回應所經過的網路路由、HTTPS、Cloudflare 和 Worker 處理時間都會計入結果。瀏覽器無法傳送 ICMP 回顯封包，因此這裡的數值可能高於命令列 Ping，只應視為網頁回應延遲的近似參考。",
    fraudCard: {
      eyebrow: "COMPREHENSIVE ASSESSMENT",
      title: "IP 欺詐值",
      bandLow: "低風險",
      bandMedium: "中等風險",
      bandHigh: "高風險",
      colSource: "資料來源",
      colValue: "參數",
      colStatus: "評估",
      statusLow: "✓ 低風險",
      statusMedium: "⚠ 中等",
      statusHigh: "✗ 高風險",
      proxyYes: "資料庫確認 · 代理標記",
      proxyNo: "未發現 · 代理標記",
      asnHosting: "✗ 機房/數據中心",
      asnIsp: "僅供參考 · 不作欺詐結論",
      osintEntries: "筆 Feodo 名單紀錄已檢查",
      osintMatch: "資料庫確認 · 命中殭屍網路 C2",
      osintNoMatch: "未發現 · 僅限此名單",
      evidenceNoData: "沒有資料",
      note: evidenceZhTW.reputationNote,
      authScamalytics: "（IP 信譽分數來源）",
      authIp2Location: "（代理屬性來源）",
      authAbuseIPDB: "（社群濫用評分來源）",
      authIpinfo: "（僅作 ASN 參考）",
      authOsint: "（殭屍網路 C2 名單 · CC0 OSINT）",
      providerInfoLabel: "了解 {provider} 的用途以及 Caylet 為什麼採用它",
      providerInfo: {
        scamalytics: {
          title: "Scamalytics 是做什麼的？",
          body: evidenceZhTW.reputationNote
        },
        ip2location: {
          title: "IP2Location 是做什麼的？",
          body: "用途：IP2Location 提供 IP 地理位置與網路資料，以及目前可用方案所回傳的代理屬性。\n\n採用原因：Caylet 將代理屬性作為獨立的分類交叉檢查，不會把它加入綜合數值。沒有代理標記也不能證明該 IP 一定沒有使用代理。"
        },
        abuseIpDb: {
          title: "AbuseIPDB 是做什麼的？",
          body: evidenceZhTW.abuseNote
        },
        ipinfo: {
          title: "IPinfo.io 是做什麼的？",
          body: "用途：IPinfo Lite 提供基本的網路歸屬背景，包括自治系統編號（ASN）和 AS 組織名稱。\n\n採用原因：ASN 可以協助說明由哪個網路宣告該 IP。Caylet 不會把 Lite 結果當作欺詐分數，也不會將其加入綜合數值。"
        },
        abuseCh: {
          title: "abuse.ch 是做什麼的？",
          body: "用途：abuse.ch 的 Feodo Tracker 發布推薦封鎖名單，列出與其追蹤的殭屍網路命令與控制（C2）基礎設施有關的 IP。\n\n採用原因：Caylet 在伺服器端下載該名單並進行本機精確比對。命中是範圍有限的正向風險證據；未命中僅表示不在這份名單中，並不能證明安全。此結果不計入綜合數值。"
        }
      }
    },
    descriptions: {
      connection: "分析由 Cloudflare 邊緣網路偵測到的網路連線特徵，包含公網 IP 地址、協定版本 (IPv4/IPv6)、地理位置、自治系統 (ASN) 及 ISP 電信商資訊。地理位置為基於網絡出口節點的估計值。",
      reputation: "顯示可取得的 Scamalytics 信譽分數和供應商可選標記。缺失標記保持未知；否定或缺失標記不能證明 IP 安全。Caylet 綜合評分會另行標示。",
      browser: "讀取瀏覽器端公開的設備特徵（如時區、語言、作業系統、螢幕解析度與 CPU 核心數），並交叉比對「設備時區」與「IP 所在地時區」是否一致。Caylet 僅在記憶體中比對，絕不建立持久裝置指紋。",
      protocol: "觀察當前連線使用的 IP 協定類型，並透過標準 WebRTC 介面進行邊界測試，確認是否存在額外的公網 IP 或路由差異。"
    }
  },
  risk: {
    low: "低",
    medium: "中",
    high: "高",
    unknown: "未知",
    lowText: "在可用的資料供應商結果中，未發現明顯的公網 IP 信譽警示。",
    mediumText: "資料供應商結果包含需要配合使用情境查看的訊號。",
    highText: "資料供應商結果包含較高的公網信譽訊號。",
    unknownText: "目前無法取得信譽評估。",
    cayletNote: "風險評級由 Caylet 依據數據綜合評估，非第三方官方唯一標準。"
  },
  diagnostics: {
    title: "金融風控綜合一致性診斷",
    noAnomaly: "[未見明顯警示] 目前可用檢測結果一致",
    incomplete: "[資料覆蓋不完整] 部分檢測項目不可用",
    mediumRisk: "[中度審視] 包含需配合情境查看的訊號",
    highRisk: "[高度警示] 偵測到顯著風控矛盾訊號",
    chipTzMatch: "時區符合",
    chipTzMismatch: "時區不符",
    chipGpuUnclassified: "GPU 類型未經驗證",
    chipGpuVirtual: "雲端模擬器 GPU",
    chipWebDriverNotExposed: "未暴露 WebDriver",
    chipWebDriverDetected: "自動化腳本",
    chipThreatClear: "可用資料未見標記",
    chipThreatUnknown: "威脅檢測資料不完整",
    chipThreatFlagged: "有威脅警示"
  },
  interpretation: {
    eyebrow: "風險脈絡",
    title: "結合實際情境閱讀數據",
    body: "這些觀察描述的是公開網路特徵，而不是你的身分或金融機構對你的信任判斷。",
    corePrinciple: "金融機構通常不會因為某一個訊號異常，就直接認定帳戶被盜。真正重要的是多個訊號之間是否互相一致，以及這次操作是否符合客戶過往行為。IP 位址、地理位置、裝置資料等單一訊號本身不足以作為可靠的身份驗證手段；但與裝置信任、行為分析和交易監控結合後，就能成為有效的風險控制。",
    signalWeightsTitle: "訊號的相對權重",
    signalTiers: [
      {
        label: "高權重訊號",
        signals: [
          "已知且可信的裝置 + App/作業系統完整性",
          "Passkey 或硬體安全金鑰",
          "IP 具有濫用、代理或匿名服務紀錄",
          "不可能旅行（如幾分鐘內跨越兩個國家）",
          "Session Token 從陌生位置被重放",
          "登入後執行敏感操作（新增收款人、大額轉帳）",
          "偵測到遠端控制或自動化操作",
          "與帳戶長期歷史一致"
        ]
      },
      {
        label: "中等權重訊號",
        signals: [
          "IP 所在國家與城市（尤其是首次出現的國家）",
          "ASN 類型（住宅、行動、機房、VPN）",
          "全新瀏覽器或作業系統",
          "登入時間異常",
          "時區不一致",
          "裝置語言突然改變",
          "行為生物特徵偏差",
          "Session 中 IP 快速輪換"
        ]
      },
      {
        label: "低權重（但可疊加）",
        signals: [
          "單獨的螢幕解析度或字型清單",
          "單獨的非美國時區",
          "單獨的海外 IP（真實旅行不等於欺詐）",
          "單獨的瀏覽器版本變更",
          "單獨使用公共 Wi-Fi"
        ]
      }
    ],
    networkTitle: "各種網路環境的風險參考",
    networkEnvironments: [
      { label: "家庭住宅寬頻", risk: "低", note: "長期使用、同一裝置、同一地區，帳戶接管風險低。家庭電腦仍可能感染惡意軟體。" },
      { label: "手機原生行動網路（大型電信商）", risk: "低至中低", note: "屬自然使用場景。IP 經常變化，城市定位可能不準；漫遊 eSIM 的出口可能在另一個國家。" },
      { label: "飯店 / 機場 / 咖啡廳公共 Wi-Fi", risk: "低至中等", note: "銀行主要看到公共出口 IP。已知裝置 + 官方 App + MFA 登入時，帳戶接管風險為中等；若新增收款人或大額轉帳，組合風險會大幅上升。" },
      { label: "一般商業 VPN", risk: "中等至中高", note: "已知機房 ASN、出口 IP 被大量用戶共用、有匿名服務標記。若客戶長期固定使用同一 VPN，風控模型可能已學習為正常行為。" },
      { label: "機房 / VPS 出口", risk: "中高至高", note: "詐騙者容易大量租用，與一般消費者登入環境差異大。NIST 將雲端服務 IP 列為可能觸發額外風控的訊號。" },
      { label: "住宅代理", risk: "中等至高（差異極大）", note: "IP 被歸類為住宅不代表使用者真的住在那裡。使用歷史無法掌控，與裝置/位置矛盾並快速輪換時最危險。" },
      { label: "Tor", risk: "極高", note: "Tor 出口節點公開且被大量匿名用戶共用，被各大金融風控系統標記。Microsoft 將 Tor 明確列為匿名 IP 風險類型。" },
      { label: "真實海外 IP（真的在旅行）", risk: "取決於整體情境", note: "真實海外 IP 本身不等於欺詐。風險取決於：是否為已知裝置、驗證強度、帳戶歷史和交易類型。真實穩定的海外行動網路有時比頻繁輪換的「美國住宅代理」更可信。" }
    ],
    scenarioTitle: "典型場景示意風險",
    riskFactor: "危險係數",
    scenarioNote: "以下不是任何機構的真實評分，僅用於說明：同一個 IP，在不同裝置、驗證方式和交易情境下，可能得到完全不同的風險評價。",
    scenarios: [
      { scenario: "已知手機 + 原生行動網路 + Passkey + 查看餘額", risk: "5–15" },
      { scenario: "已知電腦 + 家庭寬頻 + MFA + 正常使用", risk: "5–20" },
      { scenario: "已知手機在海外 + 當地行動網路", risk: "15–30" },
      { scenario: "已知裝置 + 飯店 Wi-Fi + 正常登入", risk: "20–35" },
      { scenario: "已知裝置 + 商業 VPN + 無敏感操作", risk: "30–50" },
      { scenario: "新裝置 + 新國家 + 住宅寬頻 + 強式驗證", risk: "35–55" },
      { scenario: "機房 VPN + 新裝置 + 密碼 + SMS OTP", risk: "55–75" },
      { scenario: "美國住宅代理 + 位置與裝置歷史嚴重矛盾", risk: "60–85" },
      { scenario: "新裝置 + 新國家 + 剛重設密碼 + 新增收款人", risk: "70–90" },
      { scenario: "Root/模擬器裝置 + 遠端控制 + 自動化操作", risk: "80–95" },
      { scenario: "Tor + 新裝置 + 密碼登入 + 大額轉帳", risk: "90–100" }
    ],
    conclusion: "許多人以為只要用「住宅 IP」就能安全登入海外網銀，這其實是個迷思。現代風控系統看重的是「信任鏈」：常用的裝置、無異常的網路、雙重驗證，以及符合歷史習慣的操作。與其刻意把表面訊號偽裝得天衣無縫，不如保持真實且穩定的連線。在安全工程的角度裡，「真實與規律」永遠比「刻意偽裝」來得可靠。",
    limits: [
      "IP 資料庫可能有延遲或缺漏。",
      "地理位置是近似值，也可能代表網路出口節點。",
      "動態和行動網路 IP 可能快速變化。",
      "金融機構還會使用本工具無法呈現的更多私有訊號。",
      "結果不代表任何平台的內部判斷。"
    ]
  },
  esim: {
    eyebrow: "CAYLET eSIM",
    title: "準備跨國旅行？使用 Caylet eSIM 保持行動網路連線。",
    body: "探索適合國際旅行的數位流量方案。網路路由和出口位置會隨方案與電信商而變化。",
    cta: "探索 Caylet eSIM"
  },
  education: {
    eyebrow: "運作方式",
    title: "明確區分觀察結果與預測",
    items: [
      { title: "連線", body: "Cloudflare 提供本網站可見的公網 IP 與網路中繼資料。" },
      { title: "信譽", body: "Scamalytics 可能依據自身資料集提供評分與結構化指標。" },
      { title: "瀏覽器", body: "瀏覽器提供本機環境資訊；Caylet 不建立持久裝置指紋。" }
    ]
  },
  faq: {
    title: "常見問題",
    items: [
      {
        q: "這項 IP 檢測會顯示什麼？",
        a: "顯示 Cloudflare 觀察到的公網 IP、近似網路資訊、瀏覽器提供的資訊及可用的第三方信譽指標。"
      },
      {
        q: "結果能預測銀行或支付平台的決定嗎？",
        a: "不能。相關機構會使用更多未公開訊號，本結果不代表或預測其內部審查。"
      },
      {
        q: "Caylet 會追蹤我的裝置嗎？",
        a: "不會建立持久裝置識別碼。掃描結果僅保留在目前分頁記憶體中，只會儲存語言偏好。"
      }
    ]
  },
  disclaimer: {
    title: "免責聲明",
    body: "本教育工具只報告公開網路特徵及第三方資料庫結果，不能預測、代表、繞過或影響任何銀行、發卡機構、支付平台或金融機構的內部風控。"
  },
  recommendations: {
    sectionEyebrow: "次要實驗性分析",
    sectionTitle: "實驗性場景適用度分析",
    sectionSubtitle: "根據可觀察的網路與瀏覽器訊號提供次要估算，並非對任何平台進行即時存取測試。",
    methodologyButton: "了解工具與評分邏輯",
    methodologyDialog: {
      title: "Caylet IP 如何運作",
      intro: "Caylet IP 綜合了 Cloudflare 邊緣網路數據、第三方信譽資料庫，以及您的瀏覽器特徵來進行評估。本工具僅針對您「當下的連線狀態」進行客觀分析，不會收集個人隱私，也無法絕對代表各大網路平台的最終風控結果。",
      processTitle: "檢測會觀察什麼",
      process: [
        {
          title: "連線資訊",
          body: "擷取自 Cloudflare 邊緣節點所觀察到的公網 IP 與網路特徵，確保資料真實性，無法手動輸入其他 IP。"
        },
        {
          title: "信譽資料來源",
          body: "整合 Scamalytics、AbuseIPDB、IP2Location 等多家威脅情報資料庫。我們僅在後端進行資料比對，不會將原始 API 憑證暴露給瀏覽器。"
        },
        {
          title: "瀏覽器特徵",
          body: "偵測您的時區、作業系統、螢幕解析度、硬體訊號及 WebRTC 狀態，這些資料僅在記憶體中進行瞬時比對，不會被永久儲存。"
        },
        {
          title: "地區服務可用性",
          body: "針對 ChatGPT、Claude、TikTok 等主流服務，交叉比對您的 IP 所在地區是否支援。未列出的服務則不予評估。"
        }
      ],
      fraudTitle: "IP 欺詐評分",
      fraudBody: evidenceZhTW.reputationNote,
      recommendationTitle: "場景適用度",
      recommendationBody: "我們透過一系列規則來評估您的網路環境是否適合特定場景（如社群媒體或 AI 服務）。評估維度包含：IP 信譽、網路屬性、環境一致性、瀏覽器特徵及地區限制。為求嚴謹，當系統收集到的資料過少（可信度低於 40%）時，我們將不會顯示具體分數；若可信度介於 40–59%，分數將標示為「有限估算」；而唯有可信度達到 85% 以上，才有可能獲得最高等級的安全評級。",
      gradesTitle: "分數區間（可信度達到 40% 後顯示）",
      grades: [
        "85–100：非常適合",
        "70–84：較適合",
        "55–69：一般／可以嘗試",
        "40–54：風險偏高",
        "0–39：不建議"
      ],
      sourcesTitle: "地區可用性資料來源",
      sourcesIntro: "這些連結只支援具名服務的地區結論，並不表示本工具已從目前瀏覽器即時連線相應服務。",
      sourceVerified: "複核於 {date}",
      limitsTitle: "重要限制",
      limits: [
        "供應商紀錄與近似地理位置可能不完整、延遲或存在錯誤。",
        "資料中心標記為否，並不能證明 IP 屬於住宅、行動網路或可信連線。",
        "瀏覽器觀察只提供有限訊號，不能證明身分、安全性或隱私狀態。",
        "本工具不會執行 DNS 洩漏檢測。",
        "結果不能預測銀行、支付服務商、社交平台或 AI 服務的決定。"
      ],
      modelNote: "本頁說明目前實作的實驗性模型（版本 1.6.0）。每張推薦卡會列出實際因素、可信度、缺失資料與分數上限。"
    },
    socialMediaTitle: "社交媒體網路環境適用度",
    aiSoftwareTitle: "AI 服務網路環境適用度",
    disclaimer: "此實驗性分析不會連線、登入或即時測試任何具名平台，也不代表平台官方判定。帳戶歷史、裝置紀錄、服務政策變化和使用行為都可能令實際結果不同。",
    confidenceLabel: "評估可信度",
    confidenceModal: {
      title: "評估可信度計算明細",
      subtitle: "展示各維度權重覆蓋率與上限約束規則的計算過程。",
      rawScoreLabel: "原始數據覆蓋得分",
      finalScoreLabel: "最終評估可信度",
      earnedLabel: "已獲取",
      missingLabel: "未獲取",
      items: {
        fraudScore: "Caylet 綜合 IP 風險分值 (12%)",
        networkType: "網路類型判定 (12%)",
        proxy: "Proxy 代理檢測標記 (8%)",
        vpn: "VPN 連線檢測標記 (8%)",
        tor: "Tor 匿名網路標記 (8%)",
        hosting: "Datacenter 數據中心檢測 (8%)",
        residentialProxy: "住宅代理風控標記 (8%)",
        ipCountry: "IP 地理位置國家 (6%)",
        ipTimezone: "IP 所在時區 (5%)",
        browserTimezone: "裝置系統時區 (5%)",
        automationDetected: "WebDriver 自動化檢測 (8%)",
        webrtc: "WebRTC 公網 IP 觀察 (6%)"
      },
      providerCapNote: "受單一 IP 資料供應商限制，最高可信度封頂為 80%。",
      privacyCapNote: "因核心風控欄位缺失，最高可信度封頂為 55%。"
    },
    detailsToggle: "查看詳細評分因子與分析",
    positiveTitle: "正面因素",
    warningTitle: "風險因素",
    missingTitle: "資料缺失",
    appliedCapsTitle: "觸發最高分硬性上限",
    capMaxScore: "最高分限制為 {score} 分",
    methodologyTitle: "評分方法說明",
    methodologyBody: "此實驗性啟發式指標依使用場景評估不同的可觀察維度。未知資料不會被視為安全；可信度低於 40% 時不顯示數值適用度與進度條，40–59% 時明確標為有限估算，最高等級要求可信度至少達到 85%。",
    dimensions: {
      ipReputation: "IP 信譽評分",
      networkAuth: "網路身份屬性",
      consistency: "環境一致性",
      deviceIntegrity: "裝置與瀏覽器完整性",
      protocolIntegrity: "協定與洩漏狀態",
      regionalAvailability: "服務地區可用性"
    },
    grades: {
      excellent: "非常適合",
      good: "較適合",
      moderate: "可以嘗試",
      poor: "存在明顯風險",
      high_risk: "不建議用於重要營運",
      insufficient_data: "資料不足"
    },
    confidenceLevels: {
      high: "高可信度",
      medium: "中等可信度",
      limited: "有限可信度",
      insufficient: "資料不足"
    },
    signals: {
      lowFraudScore: { label: "IP 信譽良好的紀錄", exp: "威脅數據庫標示此 IP 風險分數較低 ({score}/100)，未見重大惡意活動紀錄。" },
      elevatedFraudScore: { label: "IP 風險評分偏高", exp: "威脅數據庫標示此 IP 具有中高風險指數 ({score}/100)，可能曾參與異常網絡活動。" },
      missingFraudScore: { label: "未取得 IP 風險評分", exp: "目前設定的評分來源沒有提供有效的 IP 風險分值。" },
      osintBlocklistMatch: { label: "命中殭屍網路 C2 封鎖名單", exp: "abuse.ch Feodo Tracker 推薦名單將此 IP 匹配為殭屍網路命令與控制基礎設施。該輔助命中不會轉換為風險機率，也不能用於判定 VPN、Proxy、Tor 或資料中心屬性。" },
      mobileIsp: { label: "行動行動電信網路", exp: "此 IP 屬於行動行動電信網路，接近普通用戶日常連線特徵。" },
      residentialIsp: { label: "家用住宅寬頻網路", exp: "此 IP 屬於家用住宅寬頻網路，接近普通用戶日常連線特徵。" },
      hostingNetwork: { label: "資料中心與機房網路", exp: "此 IP 屬於雲端伺服器或資料中心機房 (Hosting)，非一般家用寬頻出口。" },
      torNode: { label: "Tor 匿名洋蔥網路出口", exp: "此 IP 經證實為 Tor 匿名節點出口，隱私高度匿名化。" },
      publicProxy: { label: "公開代理伺服器 (Public Proxy)", exp: "此 IP 被標記為公開可存取的代理連線伺服器。" },
      proxyDetected: { label: "Proxy 代理連線節點", exp: "連線經過 Proxy 代理伺服器轉發。" },
      residentialProxy: { label: "住宅代理節點 (Residential Proxy)", exp: "連線被標記為租用型住宅代理 (Residential Proxy)。" },
      vpnDetected: { label: "商業 VPN 跨區節點", exp: "連線經過 Known VPN 商業伺服器加密轉接。" },
      missingProxyCheck: { label: "未取得 Proxy 代理判定", exp: "未能從當前數據源確認 Proxy 狀態。" },
      missingVpnCheck: { label: "未取得 VPN 判定", exp: "未能從當前數據源確認 VPN 狀態。" },
      missingTorCheck: { label: "未取得 Tor 匿名判定", exp: "未能從當前數據源確認 Tor 節點狀態。" },
      missingHostingCheck: { label: "未取得資料中心判定", exp: "未能從當前數據源確認 Hosting 屬性。" },
      tzMatch: { label: "裝置時區與 IP 位置符合", exp: "裝置本機時區 ({tz}) 與 IP 出口地理時區完全一致。" },
      tzMismatch: { label: "裝置時區與 IP 位置不一致", exp: "裝置時區 ({browserTz}) 與 IP 所在地時區 ({ipTz}) 不符，可能使用了跨區代理或未調整時區。" },
      automationDetected: { label: "偵測到自動化操控標記", exp: "瀏覽器暴露了 WebDriver 或自動化測試框架痕跡。" },
      virtualGpu: { label: "虛擬機或軟體模擬渲染器", exp: "顯示晶片被識別為虛擬/軟體渲染器 ({gpu})，常見於 VPS、雲端模擬器或無頭瀏覽器。" },
      noWebRtcLeak: { label: "未觀察到額外 WebRTC 位址", exp: "本次有限觀察未暴露另一個公網 IP，但不能據此證明不存在洩漏。" },
      webrtcLeakMismatch: { label: "WebRTC IP 不一致", exp: "WebRTC 介面暴露了另一個外部 IP ({ip})，與目前連線 IP 不符。" },
      aiRegionRestricted: { label: "具名 AI 服務受到地區限制", exp: "IP 地區 {country} 的官方地區清單顯示不可用服務為：{unavailable}。" },
      aiRegionRestrictedLimited: { label: "具名 AI 服務受到地區限制", exp: "IP 地區 {country} 的官方地區清單顯示不可用服務為：{unavailable}；有限支援為：{limited}。" },
      aiRegionPartial: { label: "具名 AI 服務覆蓋不完整", exp: "IP 地區 {country} 的官方地區清單顯示：不可用為 {unavailable}；可用為 {supported}。" },
      aiRegionSupported: { label: "具名 AI 服務列出此地區", exp: "目前官方地區清單已為此地區列出這些服務：{supported}。" },
      aiRegionUnknown: { label: "未驗證特定 AI 服務地區可用性", exp: "此 IP 地區沒有已維護的具名服務政策資料，不能只憑國家作出判定。" },
      socialRegionRestricted: { label: "具名國際社交服務受限", exp: "目前可追溯的存取資料顯示，IP 地區 {country} 無法使用這些服務：{unavailable}。" },
      socialRegionPartial: { label: "TikTok 未在香港提供服務", exp: "TikTok 已退出香港市場；此結論不表示 Facebook、Instagram、X 或其他社交平台不可用。" },
      socialRegionUnknown: { label: "未驗證社交服務地區可用性", exp: "此 IP 地區沒有已維護的具名社交服務存取資料。" }
    },
    caps: {
      tor: "Tor 匿名洋蔥網路出口節點",
      publicProxy: "公開 Proxy 代理伺服器",
      proxy: "Proxy 代理伺服器",
      residentialProxy: "租用型住宅代理節點 (Residential Proxy)",
      vpnHosting: "機房與 VPN 雙重匿名連線",
      automation: "偵測到 WebDriver 自動化操控標記",
      fraudHigh: "IP 威脅分值極高 (Fraud Score >= 80)",
      fraudElevated: "IP 威脅分值偏高 (Fraud Score >= 65)",
      webrtcLeak: "WebRTC 暴露異地公開 IP",
      dualStackMismatch: "IPv4 與 IPv6 屬不同出口國家",
      hostingSocial: "雲端伺服器與資料中心 IP (Social Media)",
      hostingAi: "雲端伺服器與資料中心 IP (AI Software)",
      socialServicesRestricted: "此 IP 地區的具名國際社交服務受限",
      socialServicesPartial: "TikTok 未在香港提供服務",
      aiServicesRestricted: "具名 AI 服務受到地區限制",
      aiServicesPartial: "具名 AI 服務覆蓋不完整"
    },
    summaries: {
      insufficient: "目前證據不足，無法形成可靠的適用度評估。請查看下方資料缺失說明；介面不會顯示數值分數。",
      withWarnings: "環境展現出 {positives}，但受 {warnings} 影響，相對適合度受到一定限制。",
      optimal: "目前可用訊號中未發現明顯警示，但缺失檢測項與供應商覆蓋仍會限制此啟發式結果。",
      fallbackPositives: "基礎環境特徵"
    }
  },
  privacyModal: {
    title: "隱私政策 — Caylet IP",
    body: "Caylet IP (ip.caylet.com) 是由 Caylet LLC 提供、以隱私為核心的教育型網路診斷工具。\n\n1. 資料處理：我們僅分析 Cloudflare 邊緣網路觀察到的公網 IP 位址、連線中繼資料與網路訊號，用於即時生成檢測診斷報告。\n\n2. 本機瀏覽器環境：裝置特徵（如時區、螢幕解析度、GPU 顯示晶片、WebRTC 候選 IP）完全在您的瀏覽器分頁記憶體中處理。\n\n3. 無持久追蹤：我們不會建立持久裝置指紋、不追蹤瀏覽行為，亦不使用跨站 Cookie。僅在瀏覽器本機儲存您的語言偏好設定。\n\n4. 第三方服務：信譽評分可能檢索自 Scamalytics 與 AbuseIPDB 等公開情報庫，我們不會向第三方透露您的個人身分資料。"
  },
  termsModal: {
    title: "使用條款 — Caylet IP",
    body: "當您造訪或使用 Caylet IP (ip.caylet.com) 時，即表示您同意以下條款：\n\n1. 教育與診斷用途：本網站提供的網路特徵與風控指標僅供教育參考與網路診斷使用。\n\n2. 不代表金融機構決定：檢測結果不能預測、代表、繞過或影響任何銀行、發卡機構、支付平台或金融機構的內部風控判斷。\n\n3. 合理使用規範：嚴禁任何自動化濫用、繞過請求速率限制或惡意抓取本服務。\n\n4. 免責聲明：Caylet LLC 按「現狀 (As-Is)」提供本診斷工具，不提供任何明示或暗示之擔保。"
  },
  footer: {
    privacy: "隱私政策",
    terms: "使用條款",
    copyright: "© 2024-{year} Caylet LLC. All Rights Reserved.",
    privacyStatement: "本網站不會收集個人資料、儲存用戶指紋或設置非必要 Cookie。使用部分 API 時可能會向第三方發出最低限度的請求，分析工具等第三方服務也可能暫時啟用。"
  },
  articles: {
    title: "科普文章",
    subtitle: "了解 IP、網路環境、代理偵測、瀏覽器指紋與平台風控的基本原理。",
    emptyState: "科普文章正在整理中。你仍然可以先使用 Caylet IP 檢測工具分析目前的網路環境。",
    backToTool: "返回 IP 檢測工具",
    readTime: "分鐘閱讀",
    lastUpdated: "最後更新",
    published: "發布於",
    related: "相關文章",
    tableOfContents: "目錄",
    sources: "資料來源",
    faq: "常見問題",
    featured: "精選文章",
    all: "全部文章"
  },
  errors: {
    generic: "網路檢測未能完成，但本機瀏覽器資訊仍可查看。",
    invalid: "服務拒絕了無效請求。",
    rateLimited: "短時間內請求過多，請稍後再試。",
    turnstile: "請完成驗證後重試。"
  },
  a11y: {
    skip: "跳至主要內容",
    menu: "開啟導覽",
    scannerStatus: "IP 檢測狀態",
    external: "在新分頁開啟",
    info: "更多資訊"
  },
  seo: {
    title: "我的 IP 是什麼？IP 信譽與網路環境檢測 | Caylet",
    description: "檢測公網 IP、近似位置、ASN、網路服務商、瀏覽器環境及 Scamalytics IP 信譽指標。"
  }
} satisfies Messages;
